import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Title from '../../../components/UI/Title/Title';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import Dialog from '../../../components/UI/Dialog/Dialog';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import ClipboardCopy from '../../../components/UI/ClipboardCopy/ClipboardCopy';
import useTranslations from '../../../hooks/useTranslations';
import { IPage } from '../../../models/page';
import {
  IShippingCompany,
  IShippingCompanyCredentials,
} from '../../../models/shippingCompany';
import { IUser } from '../../../models/user';
import { useAppSelector } from '../../../store';
import { AppDispatch } from '../../../store/type';
import { ShippingCompaniesActions } from '../../../store/entities/shippingCompanies/shippingCompanies.actions';
import { UsersActions } from '../../../store/entities/users/users.actions';
import { TableActions } from '../../../store/entities/table/table.actions';
import { PERMISSIONS } from '../../../utils/permissions';
import { CONSTANTS } from '../../../utils/constants';
import {
  getDateTimeAndSeconds,
  sortDataByDate,
} from '../../../utils/formatters';
import ShippingCompany from './ShippingCompany';

import './ShippingCompany.scss';

const ShippingCompaniesManagement = ({ permission, nextRoute }: IPage) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const [selectedCompany, setSelectedCompany] =
    useState<IShippingCompany | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [companyToToggle, setCompanyToToggle] =
    useState<IShippingCompany | null>(null);
  const [companyToRelink, setCompanyToRelink] =
    useState<IShippingCompany | null>(null);
  const [newCredentials, setNewCredentials] =
    useState<IShippingCompanyCredentials | null>(null);
  const { shippingCompanies } = useAppSelector(
    (state) => state.shippingCompanies
  );
  const { users } = useAppSelector((state) => state.users);
  const { nextPageNumber, currentPage, sortModel } = useAppSelector(
    (state) => state.table
  );
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (permission === false) window.location.assign(nextRoute);
  }, [permission, nextRoute]);

  useEffect(() => {
    if (permission) {
      dispatch(ShippingCompaniesActions.getShippingCompanies([], true, 1));
      dispatch(UsersActions.getUsers());
    }
  }, [dispatch, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      dispatch(
        ShippingCompaniesActions.getShippingCompanies(
          sortModel,
          false,
          nextPageNumber
        )
      );
      dispatch(TableActions.setTableData({ nextPageNumber: null }));
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      dispatch(
        ShippingCompaniesActions.getShippingCompanies(sortModel, true, 1)
      );
    }
  }, [dispatch, sortModel]);

  const refresh = () =>
    dispatch(
      ShippingCompaniesActions.getShippingCompanies(
        sortModel,
        true,
        currentPage
      )
    );

  const toggleActive = async () => {
    if (!companyToToggle) return;
    const response = await dispatch(
      ShippingCompaniesActions.updateShippingCompany(companyToToggle.id, {
        nameHE: companyToToggle.nameHE,
        nameEN: companyToToggle.nameEN,
        allowedIps: companyToToggle.allowedIps,
        isActive: !companyToToggle.isActive,
      })
    );
    if (response?.isSucceeded) {
      setCompanyToToggle(null);
      refresh();
    }
  };
  const relink = async () => {
    if (!companyToRelink) return;
    const response = await dispatch(
      ShippingCompaniesActions.relinkShippingCompanyClient(companyToRelink.id)
    );
    if (response?.isSucceeded) {
      setCompanyToRelink(null);
      setNewCredentials({
        applicationId: response.content.applicationId,
        clientSecret: response.content.clientSecret,
      });
      refresh();
    }
  };
  const columns = [
    {
      id: 'Id',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.id'
      ),
    },
    {
      id: 'applicationId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.applicationId'
      ),
    },
    {
      id: 'nameHE',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.nameHE'
      ),
    },
    {
      id: 'nameEN',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.nameEN'
      ),
    },
    {
      id: 'isActive',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.isActive'
      ),
    },
    {
      id: 'created',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.created'
      ),
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.createdBy'
      ),
    },
    {
      id: 'lastModified',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.lastModified'
      ),
    },
    {
      id: 'lastModifiedBy',
      numeric: false,
      disablePadding: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.lastModifiedBy'
      ),
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: true,
      disableSort: true,
      label: rcTranslate(
        'configuration.shippingCompanyConfiguration.columns.actions'
      ),
    },
  ];
  const getUserFullName = (userId?: string) => {
    const user = users?.find((item: IUser) => item.userId === userId);
    return user ? `${user.firstName} ${user.lastName}`.trim() : '';
  };
  const rows = shippingCompanies.map((company) => ({
    Id: company.id,
    applicationId: (
      <span className="manageShippingCompanies__token">
        {company.applicationId}
      </span>
    ),
    nameHE: company.nameHE,
    nameEN: company.nameEN,
    isActive: rcTranslate(company.isActive ? 'common.yes' : 'common.no'),
    created: company.created ? getDateTimeAndSeconds(company.created) : '',
    createdBy: getUserFullName(company.createdBy),
    lastModified: company.lastModified
      ? getDateTimeAndSeconds(company.lastModified)
      : '',
    lastModifiedBy: getUserFullName(company.lastModifiedBy),
    actions: (
      <ActionButtons
        menuItems={[
          {
            label:
              'configuration.shippingCompanyConfiguration.actions.showDetails',
            onClick: () => setSelectedCompany(company),
          },
          {
            label: 'configuration.shippingCompanyConfiguration.actions.relink',
            onClick: () => setCompanyToRelink(company),
            show:
              company.isActive &&
              appPermissions.includes(PERMISSIONS.UPDATE_SHIPPING_COMPANY),
          },
          {
            label: company.isActive
              ? 'configuration.shippingCompanyConfiguration.actions.deactivate'
              : 'configuration.shippingCompanyConfiguration.actions.activate',
            onClick: () => setCompanyToToggle(company),
            show: appPermissions.includes(PERMISSIONS.UPDATE_SHIPPING_COMPANY),
          },
          {
            label:
              'configuration.shippingCompanyConfiguration.actions.actionPermissions',
            onClick: () =>
              navigate(
                `/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.CHILDREN.ACTION_PERMISSIONS_LIST}?shippingCompanyId=${company.id}`
              ),
          },
        ]}
      />
    ),
  }));

  return (
    <div className="manageShippingCompanies">
      <Title
        text={rcTranslate(
          'configuration.shippingCompanyConfiguration.pageTitle'
        )}
      />
      <div className="manageShippingCompanies__actionsWrapper">
        <TableButtons
          generalButtons={[
            {
              title:
                'configuration.shippingCompanyConfiguration.addShippingCompany',
              onClick: () => setIsAddOpen(true),
              show: appPermissions.includes(PERMISSIONS.ADD_SHIPPING_COMPANY),
            },
          ]}
          tableButtons={[]}
        />
      </div>
      <div className="manageShippingCompanies__table">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={false}
          expandable={false}
          showId
        />
      </div>
      {(isAddOpen || selectedCompany) && (
        <ShippingCompany
          companyData={selectedCompany}
          onClose={() => {
            setIsAddOpen(false);
            setSelectedCompany(null);
          }}
          onSaved={(credentials) => {
            setIsAddOpen(false);
            setSelectedCompany(null);
            setNewCredentials(credentials ?? null);
            refresh();
          }}
          canAdd={appPermissions.includes(PERMISSIONS.ADD_SHIPPING_COMPANY)}
          canUpdate={appPermissions.includes(
            PERMISSIONS.UPDATE_SHIPPING_COMPANY
          )}
        />
      )}
      {newCredentials && (
        <Dialog
          title="configuration.shippingCompanyConfiguration.credentialsTitle"
          onClose={() => setNewCredentials(null)}
          showCloseButton={false}
          body={
            <div className="manageShippingCompanies__credentials">
              <p className="manageShippingCompanies__credentialsWarning">
                {rcTranslate(
                  'configuration.shippingCompanyConfiguration.credentialsWarning'
                )}
              </p>
              <div className="manageShippingCompanies__credentialRow">
                <span>
                  {rcTranslate(
                    'configuration.shippingCompanyConfiguration.fieldNames.applicationId'
                  )}
                </span>
                <strong>{newCredentials.applicationId}</strong>
                <ClipboardCopy copyText={newCredentials.applicationId} />
              </div>
              <div className="manageShippingCompanies__credentialRow">
                <span>
                  {rcTranslate(
                    'configuration.shippingCompanyConfiguration.fieldNames.clientSecret'
                  )}
                </span>
                <strong>{newCredentials.clientSecret}</strong>
                <ClipboardCopy copyText={newCredentials.clientSecret} />
              </div>
            </div>
          }
        />
      )}
      {companyToToggle && (
        <Dialog
          title={
            companyToToggle.isActive
              ? 'configuration.shippingCompanyConfiguration.deactivateTitle'
              : 'configuration.shippingCompanyConfiguration.activateTitle'
          }
          body={
            companyToToggle.isActive
              ? 'configuration.shippingCompanyConfiguration.deactivateMessage'
              : 'configuration.shippingCompanyConfiguration.activateMessage'
          }
          onClose={() => setCompanyToToggle(null)}
          onConfirm={toggleActive}
          confirmTitle={
            companyToToggle.isActive
              ? 'configuration.shippingCompanyConfiguration.actions.deactivate'
              : 'configuration.shippingCompanyConfiguration.actions.activate'
          }
        />
      )}
      {companyToRelink && (
        <Dialog
          title="configuration.shippingCompanyConfiguration.relinkTitle"
          body="configuration.shippingCompanyConfiguration.relinkMessage"
          onClose={() => setCompanyToRelink(null)}
          onConfirm={relink}
          confirmTitle="configuration.shippingCompanyConfiguration.actions.relink"
        />
      )}
    </div>
  );
};

export default ShippingCompaniesManagement;
